import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  Paper,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  Avatar,
  Fade,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FileUpload as FileUploadIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import SweetAlert2 from 'react-sweetalert2';
import { useAuth } from '../../../auth/auth_provider';
import { useLocalStorage } from '../../../auth/useLocalStorage';
import { useNavigate } from 'react-router-dom';
import Compressor from 'compressorjs';
import { headerDataForm } from '../../../data/headerCostum';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  marginBottom: theme.spacing(2),
}));

const FilePreviewCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(29, 195, 159, 0.1) 0%, rgba(255, 255, 255, 0.9) 100%)',
  border: '1px solid rgba(29, 195, 159, 0.2)',
  borderRadius: '12px',
  marginTop: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(29, 195, 159, 0.2)',
  }
}));

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
      light: '#4dd4b8',
      dark: '#14a085',
    },
    secondary: {
      main: '#8ff9ab',
      light: '#b3fac5',
      dark: '#6bc98a',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

// Configuration for different form types
const FORM_CONFIGS = {
  cctv: {
    title: 'Pengecekan CCTV',
    apiEndpoint: 'api/cctv',
    nextRoute: '/pic/nvr',
    statusOptions: [
      { value: 2, label: 'Fisik Terpasang Semua' },
      { value: 1, label: 'Fisik Terpasang Sebagian' },
      { value: 0, label: 'Fisik Belum Terpasang' }
    ],
    statusField: 'status_fisik',
    requiresPhotos: true
  },
  nvr: {
    title: 'Pengecekan NVR',
    apiEndpoint: 'api/nvr',
    nextRoute: '/pic/harddisk',
    statusOptions: [
      { value: 2, label: 'Fisik Terpasang Semua' },
      { value: 1, label: 'Fisik Terpasang Sebagian' },
      { value: 0, label: 'Fisik Belum Terpasang' }
    ],
    statusField: 'status_fisik',
    requiresPhotos: true
  },
  harddisk: {
    title: 'Pengecekan Harddisk',
    apiEndpoint: 'api/harddisk',
    nextRoute: '/pic/switch',
    statusOptions: [
      { value: 2, label: 'Fisik Terpasang Semua' },
      { value: 1, label: 'Fisik Terpasang Sebagian' },
      { value: 0, label: 'Fisik Belum Terpasang' }
    ],
    statusField: 'status_fisik',
    requiresPhotos: true
  },
  switch: {
    title: 'Pengecekan Switch',
    apiEndpoint: 'api/switch',
    nextRoute: '/pic/layar',
    statusOptions: [
      { value: 2, label: 'Fisik Terpasang Semua' },
      { value: 1, label: 'Fisik Terpasang Sebagian' },
      { value: 0, label: 'Fisik Belum Terpasang' }
    ],
    statusField: 'status_fisik',
    requiresPhotos: true
  },
  layar: {
    title: 'Pengecekan Tampilan Layar',
    apiEndpoint: 'api/layar',
    nextRoute: '/pic/sistem',
    statusOptions: [
      { value: 1, label: 'Tampilan Lengkap' },
      { value: 0, label: 'Tampilan Tidak Lengkap' }
    ],
    statusField: 'status_tampilan',
    requiresPhotos: true
  },
  sistem: {
    title: 'Pengecekan Tampilan Sistem',
    apiEndpoint: 'api/sistem',
    nextRoute: '/pic/laporan',
    statusOptions: [
      { value: 1, label: 'Tampilan System Online' },
      { value: 0, label: 'Tampilan System Offline' }
    ],
    statusField: 'status_tampilan',
    requiresPhotos: true
  },
  ceklokasi: {
    title: 'Pengecekan Lokasi',
    apiEndpoint: 'api/lokasi',
    nextRoute: '/pic/laporan/lokasi',
    statusOptions: [
      { value: 1, label: 'Lokasi Sesuai' },
      { value: 0, label: 'Lokasi Tidak Sesuai' }
    ],
    statusField: 'status_lokasi',
    requiresPhotos: true
  }
};

const UnifiedFormComponent = ({ formType, onSuccess, onError }) => {
  const navigate = useNavigate();
  const { pic } = useAuth();
  const [longwis] = useLocalStorage('longwis');
  
  const [swalProps, setSwalProps] = useState({});
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    [FORM_CONFIGS[formType].statusField]: '',
    keterangan: '',
  });

  const config = FORM_CONFIGS[formType];

  const compressImage = useCallback((file, quality = 0.7, maxWidth = 450) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality,
        maxWidth,
        success: resolve,
        error: reject,
      });
    });
  }, []);

  const uploadFile = useCallback(async (fileList) => {
    if (files.length + fileList.length > 4) {
      setSwalProps({
        show: true,
        title: 'Gagal Pilih File',
        icon: 'error',
        text: 'File Upload Maksimal 4',
      });
      return;
    }

    try {
      setLoading(true);
      const compressedFiles = [];

      for (const file of Array.from(fileList)) {
        const compressedFile = await compressImage(file);
        compressedFiles.push(compressedFile);
      }

      setFiles(prev => [...prev, ...compressedFiles]);
    } catch (error) {
      console.error('Error compressing files:', error);
      setSwalProps({
        show: true,
        title: 'Error',
        icon: 'error',
        text: 'Gagal mengkompres file',
      });
    } finally {
      setLoading(false);
    }
  }, [files.length, compressImage]);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const packFiles = useCallback(async (fileList) => {
    const formData = new FormData();
    
    fileList.forEach((file, i) => {
      formData.append(`file-${i}`, file, file.name);
    });
    
    formData.append('keterangan', data.keterangan);
    formData.append(config.statusField, data[config.statusField]);
    formData.append('id_user', pic.user.id);
    formData.append('id_longwis', longwis.id);

    return formData;
  }, [data, config.statusField, pic.user.id, longwis.id]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    
    if (config.requiresPhotos && data[config.statusField] !== 0 && files.length === 0) {
      setSwalProps({
        show: true,
        title: 'File Required',
        icon: 'warning',
        text: 'Silakan upload foto dokumentasi',
      });
      return;
    }

    try {
      setLoading(true);
      const formData = await packFiles(files);

      const response = await fetch(`${process.env.REACT_APP_API_URL}${config.apiEndpoint}`, {
        method: 'POST',
        headers: headerDataForm,
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result) {
        setSwalProps({
          show: true,
          title: 'Sukses',
          icon: 'success',
          text: 'Data telah ditambahkan',
        });
        
        if (onSuccess) {
          onSuccess(result);
        }
        
        setTimeout(() => {
          navigate(config.nextRoute, { state: { [formType]: result } });
        }, 1500);
      } else {
        throw new Error(result.message || 'Gagal menyimpan data');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSwalProps({
        show: true,
        title: 'Gagal',
        icon: 'error',
        text: error.message || 'Terjadi kesalahan',
      });
      
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [data, files, config, formType, packFiles, navigate, onSuccess, onError]);

  const getStatusIcon = (status) => {
    if (config.statusField === 'status_fisik') {
      switch (parseInt(status)) {
        case 2: return <CheckCircleIcon color="success" />;
        case 1: return <WarningIcon color="warning" />;
        case 0: return <ErrorIcon color="error" />;
        default: return null;
      }
    } else {
      switch (parseInt(status)) {
        case 1: return <CheckCircleIcon color="success" />;
        case 0: return <ErrorIcon color="error" />;
        default: return null;
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)',
        py: 4
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container maxWidth="sm">
          <CssBaseline />
          <SweetAlert2
            {...swalProps}
            didClose={() => setSwalProps({})}
          />

          {/* Header */}
          <Zoom in={true} timeout={600}>
            <StyledCard elevation={8}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 8px 32px rgba(29,195,159,0.3)'
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography 
                  variant="h4" 
                  fontWeight={700}
                  sx={{
                    background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  {config.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Silakan lengkapi form pengecekan di bawah ini
                </Typography>
              </CardContent>
            </StyledCard>
          </Zoom>

          {/* Form */}
          <Fade in={true} timeout={800}>
            <StyledCard elevation={8}>
              <CardContent sx={{ p: 4 }}>
                <Box component="form" onSubmit={handleSubmit}>
                  {/* Status Selection */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="status-label">
                      {config.statusField === 'status_fisik' ? 'Status Fisik' : 
                       config.statusField === 'status_tampilan' ? 'Status Tampilan' : 'Status'}
                    </InputLabel>
                    <Select
                      labelId="status-label"
                      value={data[config.statusField]}
                      required
                      label={config.statusField === 'status_fisik' ? 'Status Fisik' : 
                             config.statusField === 'status_tampilan' ? 'Status Tampilan' : 'Status'}
                      onChange={(e) => setData(prev => ({ 
                        ...prev, 
                        [config.statusField]: e.target.value 
                      }))}
                      startAdornment={data[config.statusField] && getStatusIcon(data[config.statusField])}
                    >
                      {config.statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(option.value)}
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* File Upload Section */}
                  {config.requiresPhotos && data[config.statusField] !== 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 3,
                          border: '2px dashed',
                          borderColor: 'primary.light',
                          borderRadius: 3,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, rgba(29,195,159,0.05) 0%, rgba(255,255,255,0.8) 100%)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(29,195,159,0.1)',
                          }
                        }}
                      >
                        <FileUploadIcon 
                          sx={{ 
                            fontSize: 48, 
                            color: 'primary.main', 
                            mb: 2 
                          }} 
                        />
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Upload Dokumentasi Foto
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Pilih atau drag & drop file gambar di sini
                        </Typography>
                        
                        <Button
                          component="label"
                          variant="contained"
                          startIcon={<CloudUploadIcon />}
                          disabled={loading}
                          sx={{
                            background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #14a085 0%, #0f8c73 100%)',
                            }
                          }}
                        >
                          {loading ? 'Mengompres...' : 'Pilih File'}
                          <VisuallyHiddenInput
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => uploadFile(e.target.files)}
                          />
                        </Button>
                        
                        {loading && (
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress />
                          </Box>
                        )}
                      </Paper>
                      
                      <Alert 
                        severity="info" 
                        sx={{ mt: 2, borderRadius: 2 }}
                      >
                        <Typography variant="body2">
                          <strong>Format:</strong> JPG, PNG • <strong>Maksimal:</strong> 4 file, 5MB per file
                        </Typography>
                      </Alert>
                    </Box>
                  )}

                  {/* File Preview */}
                  {files.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        File Terpilih ({files.length}/4)
                      </Typography>
                      <Grid container spacing={2}>
                        {files.map((file, index) => (
                          <Grid item xs={12} key={index}>
                            <FilePreviewCard>
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                                    <ImageIcon />
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography 
                                      variant="body1" 
                                      fontWeight={600}
                                      sx={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {file.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatFileSize(file.size)}
                                    </Typography>
                                  </Box>
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => removeFile(index)}
                                    startIcon={<DeleteIcon />}
                                  >
                                    Hapus
                                  </Button>
                                </Box>
                              </CardContent>
                            </FilePreviewCard>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Description Field */}
                  {data[config.statusField] !== 2 && data[config.statusField] !== 1 && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Keterangan"
                      placeholder="Masukkan keterangan tambahan..."
                      value={data.keterangan}
                      onChange={(e) => setData(prev => ({ 
                        ...prev, 
                        keterangan: e.target.value 
                      }))}
                      sx={{ mb: 3 }}
                    />
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                      }
                    }}
                  >
                    {loading ? 'Menyimpan...' : 'Selanjutnya'}
                  </Button>
                </Box>
              </CardContent>
            </StyledCard>
          </Fade>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default UnifiedFormComponent;
